"use client";

import Link from "next/link";
import { Footer } from "@/app/components/Footer";
import { Image } from "@/app/components/Image";
import NavbarSnowsense from "@/app/components/NavbarSnowsense";
import { ScrollReveal } from "@/app/components/ScrollReveal";
import {
	ArrowIcon,
	ArrowLink,
	Container,
	HeroImage,
	Pill,
	RuleBlock,
	Section,
	SitePage,
} from "@/app/components/SiteUi";
import { useI18n } from "@/lib/i18n";

const rowClass =
	"grid grid-cols-[auto_minmax(0,1fr)] items-stretch gap-[clamp(40px,6vw,96px)] py-[clamp(28px,4vh,44px)] max-nav:grid-cols-1 max-nav:gap-6";
const rowReversedClass =
	"grid items-stretch gap-[clamp(32px,4vw,64px)] py-[clamp(28px,4vh,44px)] nav:grid-cols-[minmax(0,620px)_auto] nav:justify-start max-nav:grid-cols-1 max-nav:gap-6 [&>*:first-child]:order-2 max-nav:[&>*:first-child]:order-none";
const figureClass =
	"w-[clamp(300px,34vw,460px)] max-w-full overflow-hidden rounded-lg shadow-[0_16px_40px_rgba(41,51,92,.12)] max-nav:w-[min(100%,360px)]";
const photoFigureClass = `${figureClass} h-[min(560px,72vh)] max-nav:h-auto`;
const photoClass =
	"h-full w-full object-cover transition-transform duration-[600ms] ease-[cubic-bezier(.2,.8,.2,1)] motion-safe:hover:scale-[1.03]";
const rowTitleClass = "text-[clamp(28px,2.8vw,42px)] font-bold leading-[1.1] tracking-[-0.015em] text-gray-900";
const bodyClass = "text-[19px] text-gray-600";
const bulletClass =
	"relative pl-5 before:absolute before:left-0 before:top-[0.6em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-primary-500";

export default function UmFyrirtaekid() {
	const { t } = useI18n();

	return (
		<SitePage>
			<NavbarSnowsense />

			<main>
				<Section theme="photo" className="h-[clamp(432px,calc(72px+(100vh-72px)*.55),692px)]">
					<HeroImage src="/pictures/pano.png" origin="50% 50%" priority />
					<div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,14,28,.7)_0%,rgba(10,14,28,.25)_22%,rgba(0,0,0,.05)_50%,rgba(0,0,0,.55)_100%)]" />
					<div className="absolute inset-x-0 bottom-[clamp(36px,6vh,64px)]">
						<Container>
							<h1 className="text-[clamp(40px,5vw,72px)] font-bold leading-none tracking-[-0.02em] [text-shadow:0_2px_24px_rgba(0,0,0,.35)]">
								{t("products_page.banner_title")}
								<span aria-hidden="true" className="mt-3 block h-[3px] w-16 rounded-full bg-white/80" />
							</h1>
						</Container>
					</div>
				</Section>

				<Section theme="light">
					<Container className="pb-[clamp(24px,4vh,40px)] pt-[clamp(16px,3vh,32px)]">
						<ScrollReveal className="block">
							<h2 className="pt-6 text-[clamp(28px,2.8vw,42px)] font-bold leading-[1.1] tracking-[-0.015em] text-gray-900">
								{t("products_page.heading")}
							</h2>
						</ScrollReveal>

						<ScrollReveal className="block scroll-mt-[88px]" id="basepack">
							<article className={rowClass}>
								<figure className={`${figureClass} aspect-[1440/1920]`}>
									<Image
										className="h-full w-full object-cover"
										src="/pictures/skyrimynd.jpg"
										alt="SM4 Base Kit: sensor unit with temperature cable"
									/>
								</figure>
								<RuleBlock className="max-w-[600px] justify-start self-start">
									<h3 className={rowTitleClass}>{t("products_page.base_pack_title")}</h3>
									<p className={bodyClass}>{t("products_page.base_pack_intro")}</p>
									<ul className="flex flex-col gap-2 text-[18px] leading-normal text-gray-600">
										<li className={bulletClass}>{t("products_page.base_pack_item_cable")}</li>
										<li className={bulletClass}>{t("products_page.base_pack_item_laser")}</li>
										<li className={bulletClass}>{t("products_page.base_pack_item_logger")}</li>
										<li className={bulletClass}>
											{t("products_page.base_pack_item_connectivity")}
										</li>
										<li className={bulletClass}>{t("products_page.base_pack_item_data_access")}</li>
										<li className={bulletClass}>{t("products_page.base_pack_item_brackets")}</li>
									</ul>
									<p className="pt-6 text-[15px] text-gray-500">
										{t("products_page.base_pack_note")}
									</p>
								</RuleBlock>
							</article>
						</ScrollReveal>

						<ScrollReveal className="block scroll-mt-[88px]" id="withpole">
							<article className={`${rowReversedClass} border-t border-gray-200`}>
								<figure className={photoFigureClass}>
									<Image
										className={photoClass}
										src="/pictures/new/sud_yfir_bae.jpeg"
										alt="SM4 with pole and aluminum foundation, installed above a coastal town"
									/>
								</figure>
								<div className="flex flex-col justify-center">
									<RuleBlock className="max-w-[600px] self-start">
										<h3 className={rowTitleClass}>{t("products_page.with_pole_title")}</h3>
										<p className={bodyClass}>{t("products_page.with_pole_body")}</p>
										<ArrowLink href="/snowsense/sm4sensors" className="mt-1 self-start">
											{t("navbar_about_snowsense.about_sm4")}
										</ArrowLink>
									</RuleBlock>
								</div>
							</article>
						</ScrollReveal>
					</Container>
				</Section>

				<Section theme="tint" className="border-t border-gray-200">
					<Container>
						<ScrollReveal className="block scroll-mt-[88px]" id="seasensor">
							<article className={rowClass}>
								<figure className={photoFigureClass}>
									<Link href="/snowsense/seasensors" className="block h-full">
										<Image
											className={photoClass}
											src="/pictures/sjom.jpeg"
											alt="Sea temperature sensor mounted on a fish farm pen"
										/>
									</Link>
								</figure>
								<div className="flex flex-col justify-center">
									<RuleBlock className="max-w-[600px] self-start">
										<h3 className={rowTitleClass}>{t("products_page.sea_title")}</h3>
										<p className={bodyClass}>{t("products_page.sea_body")}</p>
										<ArrowLink href="/snowsense/seasensors" className="mt-1 self-start">
											{t("navbar_about_snowsense.about_sea")}
										</ArrowLink>
									</RuleBlock>
								</div>
							</article>
						</ScrollReveal>
					</Container>
				</Section>

				<Section theme="light" id="custom" className="scroll-mt-[88px] py-[clamp(48px,7vh,80px)]">
					<Container>
						<ScrollReveal className="flex flex-col items-center gap-4 text-center">
							<h2 className="text-[clamp(32px,3.4vw,48px)] font-bold leading-[1.1] tracking-[-0.015em] text-gray-900">
								{t("products_page.custom_title")}
							</h2>
							<div className="flex max-w-[560px] flex-col items-center gap-5">
								<p className="text-left text-[18px] leading-[1.6] text-gray-600">
									{t("products_page.custom_body")}
								</p>
								<Pill href="/contact" variant="primary">
									{t("products_page.custom_cta")}
									<ArrowIcon />
								</Pill>
							</div>
						</ScrollReveal>
					</Container>
				</Section>
			</main>

			<Footer />
		</SitePage>
	);
}
